<script setup>
import { ref, reactive } from "vue";
import { useRouter } from "vue-router";
import AuthLayout from "@/layouts/AuthLayout.vue";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { User, Building2, Mail, Lock, ArrowRight } from "lucide-vue-next";
import api from "@/lib/axios";

const router = useRouter();
const { toast } = useToast();
const isLoading = ref(false);

const form = reactive({
  name: "",
  company: "",
  email: "",
  password: "",
});

const handleRegister = async (e) => {
  e.preventDefault();
  isLoading.value = true;

  try {
    await api.get('/sanctum/csrf-cookie');
    await api.post('/api/v1/auth/register', {
      email: form.email,
      password: form.password,
      password_confirmation: form.password,
      full_name: form.name,
      organization_name: form.company,
    });
    
    toast({
      title: "Cuenta creada correctamente",
      description: "Te hemos enviado un email para verificar tu cuenta. Por favor, revisa tu bandeja de entrada.",
    });
    router.push("/login");
  } catch (error) {
    const message = error.response?.data?.message || 
                    error.response?.data?.errors?.email?.[0] ||
                    "Error al crear la cuenta. Por favor, inténtalo de nuevo.";
    toast({
      title: "Error",
      description: message,
      variant: "destructive",
    });
  } finally {
    isLoading.value = false;
  }
};
</script>

<template>
  <AuthLayout>
    <div class="text-center mb-8">
      <h1 class="text-2xl font-bold text-foreground mb-2">Crear Cuenta</h1>
      <p class="text-sm text-muted-foreground">
        Únete a las empresas seguras con Fenix Guardian
      </p>
    </div>

    <form @submit="handleRegister" class="space-y-4">
      <div class="grid grid-cols-2 gap-4">
        <div class="space-y-2">
          <Label for="name">Nombre</Label>
          <div class="relative">
            <User class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input 
              id="name" 
              v-model="form.name"
              placeholder="Juan Pérez" 
              class="pl-9 bg-background/50 border-border/50 focus:border-accent" 
              required
            />
          </div>
        </div>
        <div class="space-y-2">
          <Label for="company">Empresa</Label>
          <div class="relative">
            <Building2 class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input 
              id="company" 
              v-model="form.company"
              placeholder="Acme Inc." 
              class="pl-9 bg-background/50 border-border/50 focus:border-accent" 
              required
            />
          </div>
        </div>
      </div>

      <div class="space-y-2">
        <Label for="email">Correo profesional</Label>
        <div class="relative">
          <Mail class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input 
            id="email" 
            type="email" 
            v-model="form.email"
            placeholder="juan@empresa.com" 
            class="pl-9 bg-background/50 border-border/50 focus:border-accent" 
            required
          />
        </div>
      </div>

      <div class="space-y-2">
        <Label for="password">Contraseña</Label>
        <div class="relative">
          <Lock class="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input 
            id="password" 
            type="password" 
            v-model="form.password"
            placeholder="Mínimo 8 caracteres" 
            class="pl-9 bg-background/50 border-border/50 focus:border-accent" 
            required
          />
        </div>
      </div>

      <Button 
        type="submit" 
        variant="hero-primary" 
        class="w-full mt-2 group" 
        :disabled="isLoading"
      >
        <span v-if="isLoading">Procesando...</span>
        <span v-else class="flex items-center gap-2">
          Registrar organización
          <ArrowRight class="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </Button>
    </form>

    <template #footer>
      <p class="text-sm text-muted-foreground">
        ¿Ya tienes una cuenta?
        <router-link 
          to="/login" 
          class="font-medium text-foreground hover:text-accent transition-colors underline underline-offset-4"
        >
          Iniciar sesión
        </router-link>
      </p>
    </template>
  </AuthLayout>
</template>
